import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, Trophy, Star, Rocket, Target } from 'lucide-react';

const About: React.FC = () => {
  const [flippedCard, setFlippedCard] = useState<string | null>(null);
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const toggleFlip = (value: string) => {
    setFlippedCard(flippedCard === value ? null : value);
  };

  const teamMembers = [
    {
      name: 'Struan Dsouza',
      role: 'Co-Founder & CEO',
      bio: 'Visionary entrepreneur driving the future of sports technology',
      image: '/images/team/struan-dsouza.jpg'
    },
    {
      name: 'Kushaj Dwivedi',
      role: 'Co-Founder & CTO',
      bio: 'AI/ML expert revolutionizing athletic performance analytics',
      image: '/team/kushaj.jpg'
    },
    {
      name: 'Team Member 3',
      role: 'Marketing, Consumer Behaviour and Sales Consultant',
      bio: 'Product innovation leader with deep sports industry expertise',
      image: '/team/member3.jpg'
    },
    {
      name: 'Team Member 4',
      role: 'Chief Science Officer',
      bio: 'Leading researcher in sports biomechanics and injury prevention',
      image: '/team/member4.jpg'
    }
  ];

  const accomplishments = [
    {
      icon: <Trophy className="w-6 h-6" />,
      title: 'AI-Enabled Prototypes',
      description: 'Successfully developed fully integrated AI-enabled shin pad prototypes'
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: 'Seamless Connectivity',
      description: 'Established robust Bluetooth (BLE) and Wi-Fi data transfer protocols'
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Intuitive Mobile App',
      description: 'Created sophisticated companion mobile app with exceptional UX'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Positive Validation',
      description: 'Received overwhelmingly positive user validation from initial testing'
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: 'Market Positioning',
      description: 'Positioned as innovative competitor to Playermaker, Soccerment, and Gengee'
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Professional Recognition',
      description: 'Trusted by Hong Kong Premier League players and elite athletes'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cresento-primary to-cresento-dark text-white py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Cresento</h1>
            <p className="text-xl md:text-2xl text-cresento-light">
              Building the Future of Football Analytics with AI-Powered Smart Shinpads
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-center mb-12 text-cresento-accent">Our Mission</h2>
            <div className="glass-colored rounded-2xl shadow-xl p-8 md:p-12">
              <p className="text-lg text-cresento-light leading-relaxed mb-6">
                The mission at Cresento is to <span className="text-cresento-accent font-semibold">democratize sports analytics</span>, making those elite level insights accessible to your everyday athletes, not just the highest level of athletes. It's an unfair monopoly of data driven insights.
              </p>
              <p className="text-lg text-cresento-light leading-relaxed mb-6">
                We are starting with football but it does not end there, from the grassroots to the highest level, in all sports, <span className="text-cresento-secondary font-semibold">every player deserves these data driven insights</span> to improve, to compete, to be seen.
              </p>
              <p className="text-lg text-cresento-light leading-relaxed mb-6">
                With our <span className="text-cresento-accent font-semibold">smart wearables and AI coach</span>, athletes will be able to access this data driven insights in an affordable and non complex form factor.
              </p>
              <p className="text-xl font-bold text-white border-l-4 border-cresento-accent pl-6">
                So any young athlete chasing a dream, Cresento can empower them to become the best versions of themselves.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-cresento-primary">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold text-center mb-12 text-cresento-accent">Our Story</h2>
            <div className="space-y-6 text-cresento-light">
              <p className="text-lg leading-relaxed">
                Cresento was born from a simple observation: while professional athletes have access to advanced performance analytics, amateur and youth players are left behind. Our founders, passionate about both technology and football, saw an opportunity to bridge this gap.
              </p>
              <p className="text-lg leading-relaxed">
                Starting in the HKUST Dream Builder Program, we developed our first prototype that could track basic metrics. The response from players was overwhelming – they wanted more. This drove us to push the boundaries of what's possible, integrating AI to provide not just data, but actionable insights.
              </p>
              <p className="text-lg leading-relaxed">
                Our breakthrough came when we realized that shin guards – essential protective equipment worn by every player – could be the perfect platform for our technology. By embedding sensors and processors into something players already wear, we created a seamless experience that doesn't interfere with the game.
              </p>
              <p className="text-lg leading-relaxed">
                Today, after winning Unicorn Day and partnering with elite clubs like Inter Milan and Juventus, we're proud to bring professional-level analytics to players at every level. From youth academies to weekend warriors, Cresento is democratizing access to the tools that make champions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Accomplishments Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-cresento-accent"
          >
            Milestones Achieved, Future Forged
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {accomplishments.map((item, index) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass rounded-xl shadow-lg p-6 hover:glass-strong transition-all hover:transform hover:scale-105 duration-300"
              >
                <div className="text-cresento-accent mb-4 flex items-center">
                  {item.icon}
                  <h3 className="text-xl font-semibold ml-3 text-white">{item.title}</h3>
                </div>
                <p className="text-cresento-light">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-cresento-primary">
        <div className="container mx-auto px-4">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-4xl font-bold text-center mb-12 text-cresento-accent"
          >
            Meet Our Team
          </motion.h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-48 h-48 mx-auto mb-4 rounded-full overflow-hidden bg-gradient-to-br from-cresento-secondary to-cresento-accent flex items-center justify-center border-4 border-cresento-secondary shadow-lg shadow-cresento-secondary/50">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.parentElement!.innerHTML = `<div class="text-white text-3xl font-bold">${member.name.split(' ').map(n => n[0]).join('')}</div>`;
                    }}
                  />
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">{member.name}</h3>
                <p className="text-cresento-accent font-medium mb-3">{member.role}</p>
                <p className="text-cresento-light text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-6xl mx-auto text-center"
          >
            <h2 className="text-4xl font-bold mb-12 text-cresento-accent">Our Values</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {/* SIMPLICITY */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                onClick={() => toggleFlip('SIMPLICITY')}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'SIMPLICITY' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
                      SIMPLICITY
                    </h3>
                    <p className="text-gray-400 text-xs mt-4">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-cresento-secondary/20 to-cresento-accent/20 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/60 flex flex-col justify-center text-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <p className="text-cresento-light text-lg leading-relaxed mb-4">
                      Our commitment to <span className="text-cresento-accent font-semibold">SIMPLICITY</span> in our design delivers a product that is effortless to use. But this simplicity is our greatest technological achievement. We have packed a state-of-the-art AI powered coach into a single shin pad, worn by you and measured by us.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* POTENTIAL */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                onClick={() => toggleFlip('POTENTIAL')}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'POTENTIAL' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-accent to-cresento-secondary bg-clip-text text-transparent mb-4">
                      POTENTIAL
                    </h3>
                    <p className="text-gray-400 text-xs mt-4">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-cresento-accent/20 to-cresento-secondary/20 rounded-2xl p-6 sm:p-8 border border-cresento-accent/60 flex flex-col justify-center text-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <p className="text-cresento-light text-lg leading-relaxed mb-4">
                      This allows us to unlock every athlete's true <span className="text-cresento-secondary font-semibold">POTENTIAL</span> by providing real, actionable insights and customized training plans.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* TRUST */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: 0.3 }}
                onClick={() => toggleFlip('TRUST')}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'TRUST' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/30 hover:border-cresento-secondary/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-secondary to-cresento-accent bg-clip-text text-transparent mb-4">
                      TRUST
                    </h3>
                    <p className="text-gray-400 text-xs mt-4">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-cresento-secondary/20 to-cresento-accent/20 rounded-2xl p-6 sm:p-8 border border-cresento-secondary/60 flex flex-col justify-center text-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <p className="text-cresento-light text-lg leading-relaxed mb-4">
                      <span className="text-cresento-accent font-semibold">TRUST</span> is in the foundation of everything we do, because we are also athletes, from listening to our community to being transparent in our business core.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>

              {/* COMMUNITY */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.6, delay: 0.4 }}
                onClick={() => toggleFlip('COMMUNITY')}
                className="flip-card-container h-64 cursor-pointer"
                style={{ perspective: '1000px' }}
              >
                <motion.div
                  className="flip-card-inner w-full h-full relative"
                  style={{ transformStyle: 'preserve-3d' }}
                  animate={{ rotateY: flippedCard === 'COMMUNITY' ? 180 : 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  {/* Front Face */}
                  <div 
                    className="flip-card-front absolute w-full h-full bg-gradient-to-br from-black/50 to-cresento-primary/30 rounded-2xl p-6 sm:p-8 border border-cresento-accent/30 hover:border-cresento-accent/60 transition-all duration-300 flex flex-col items-center justify-center text-center"
                    style={{ backfaceVisibility: 'hidden' }}
                  >
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-cresento-accent to-cresento-secondary bg-clip-text text-transparent mb-4">
                      COMMUNITY
                    </h3>
                    <p className="text-gray-400 text-xs mt-4">Click to learn more</p>
                  </div>

                  {/* Back Face */}
                  <div 
                    className="flip-card-back absolute w-full h-full bg-gradient-to-br from-cresento-accent/20 to-cresento-secondary/20 rounded-2xl p-6 sm:p-8 border border-cresento-accent/60 flex flex-col justify-center text-center"
                    style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                  >
                    <p className="text-cresento-light text-lg leading-relaxed mb-4">
                      From listening to our <span className="text-cresento-secondary font-semibold">COMMUNITY</span> to being transparent in our business core, we believe in building together with our athletes.
                    </p>
                    <p className="text-gray-400 text-xs mt-4">Click to flip back</p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cresento-secondary to-cresento-accent">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold mb-4 text-white">Join the Revolution</h2>
            <p className="text-xl mb-8 text-gray-200">Be part of the future of football analytics</p>
            <a href="/product" className="bg-white text-cresento-secondary hover:bg-gray-100 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-block">
              Explore Our Products
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;